// Event + EventInterest request/transition payload schemas. Wave 1 dual-role.
// State machines live in state-machines/{event,event-interest}.ts; the worker
// validates request bodies with these zod schemas, then constructs the typed
// EventEvent / EventInterestEvent for the machine.
import { z } from 'zod';

import { idSchema, isoDateSchema, moneyMinorSchema, richTextSchema } from './common.js';

// -----------------------------------------------------------------------------
// Event
// -----------------------------------------------------------------------------

const titleSchema = z.string().min(3).max(200);
const eventDescriptionSchema = richTextSchema(8000);

export const createEventInputSchema = z
  .object({
    venue_id: idSchema,
    title: titleSchema,
    description: eventDescriptionSchema.optional(),
    /**
     * Wave 3 — art form vocab id. Optional on the schema (legacy events lack
     * it) but encouraged for new posts. Wave 4 M will set this on create and
     * propagate to the spawned exchange instead of falling back to the
     * venue's first art form.
     */
    art_form_id: idSchema.optional(),
    /** Unix epoch ms in UTC. The UI formats relative to the venue tz. */
    starts_at: z.number().int().positive(),
    ends_at: z.number().int().positive(),
    slots_total: z.number().int().positive().max(50).default(1),
    pay_cents: moneyMinorSchema.optional(),
    pay_notes: z.string().max(500).optional(),
  })
  .refine((v) => v.ends_at >= v.starts_at, {
    message: 'ends_at must be >= starts_at',
    path: ['ends_at'],
  });
export type CreateEventInput = z.infer<typeof createEventInputSchema>;

export const updateEventInputSchema = z
  .object({
    title: titleSchema.optional(),
    description: eventDescriptionSchema.optional(),
    art_form_id: idSchema.optional(),
    starts_at: z.number().int().positive().optional(),
    ends_at: z.number().int().positive().optional(),
    slots_total: z.number().int().positive().max(50).optional(),
    pay_cents: moneyMinorSchema.optional(),
    pay_notes: z.string().max(500).optional(),
  })
  .refine(
    (v) => v.starts_at === undefined || v.ends_at === undefined || v.ends_at >= v.starts_at,
    {
      message: 'ends_at must be >= starts_at',
      path: ['ends_at'],
    },
  );
export type UpdateEventInput = z.infer<typeof updateEventInputSchema>;

export const cancelEventInputSchema = z.object({
  reason: z.string().max(500).optional(),
});
export type CancelEventInput = z.infer<typeof cancelEventInputSchema>;

export const listEventsQuerySchema = z
  .object({
    venue_id: idSchema.optional(),
    state: z.enum(['draft', 'published', 'closed', 'cancelled']).optional(),
    /** Wave 3 — date-range filter. Unix epoch ms in UTC. Inclusive bounds. */
    from: z.coerce.number().int().nonnegative().optional(),
    to: z.coerce.number().int().nonnegative().optional(),
    cursor: z.string().min(1).max(256).optional(),
    limit: z.coerce.number().int().positive().max(100).default(25),
  })
  .refine((v) => v.from === undefined || v.to === undefined || v.to >= v.from, {
    message: 'to must be >= from',
    path: ['to'],
  });
export type ListEventsQuery = z.infer<typeof listEventsQuerySchema>;

// -----------------------------------------------------------------------------
// EventInterest
// -----------------------------------------------------------------------------

export const submitInterestInputSchema = z.object({
  message: richTextSchema(2000).optional(),
});
export type SubmitInterestInput = z.infer<typeof submitInterestInputSchema>;

export const decideInterestInputSchema = z.discriminatedUnion('decision', [
  z.object({
    decision: z.literal('accept'),
    /** Inherited dates for the spawned Exchange. */
    start_date: isoDateSchema,
    end_date: isoDateSchema,
  }),
  z.object({
    decision: z.literal('decline'),
    reason: z.string().max(2000).optional(),
  }),
]);
export type DecideInterestInput = z.infer<typeof decideInterestInputSchema>;

export const withdrawInterestInputSchema = z.object({}).strict();
export type WithdrawInterestInput = z.infer<typeof withdrawInterestInputSchema>;
